/**
 * Loads Cashfree's official hosted-checkout SDK (v3) on demand and opens the
 * real checkout with a server-issued payment session id.
 */
type CashfreeInstance = {
  checkout: (options: {
    paymentSessionId: string;
    redirectTarget?: string;
  }) => Promise<{ error?: { message?: string } } | void>;
};

type CashfreeFactory = (options: { mode: "sandbox" | "production" }) => CashfreeInstance;

declare global {
  interface Window {
    Cashfree?: CashfreeFactory;
  }
}

const SDK_URL = "https://sdk.cashfree.com/js/v3/cashfree.js";
let loader: Promise<CashfreeFactory> | null = null;

export function loadCashfreeSdk(): Promise<CashfreeFactory> {
  if (typeof window === "undefined") return Promise.reject(new Error("NO_WINDOW"));
  if (window.Cashfree) return Promise.resolve(window.Cashfree);
  if (loader) return loader;

  loader = new Promise<CashfreeFactory>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SDK_URL}"]`);
    const script = existing ?? document.createElement("script");
    script.src = SDK_URL;
    script.async = true;
    script.onload = () => {
      if (window.Cashfree) resolve(window.Cashfree);
      else reject(new Error("CASHFREE_SDK_UNAVAILABLE"));
    };
    script.onerror = () => reject(new Error("CASHFREE_SDK_FAILED"));
    if (!existing) document.head.appendChild(script);
  }).catch((error) => {
    loader = null;
    throw error;
  });

  return loader;
}

/** Opens Cashfree hosted checkout in the current tab. */
export async function openCashfreeCheckout(input: {
  paymentSessionId: string;
  mode: "sandbox" | "production";
}): Promise<void> {
  const factory = await loadCashfreeSdk();
  const cashfree = factory({ mode: input.mode });
  const result = await cashfree.checkout({
    paymentSessionId: input.paymentSessionId,
    redirectTarget: "_self",
  });
  const message = (result as { error?: { message?: string } } | undefined)?.error?.message;
  if (message) throw new Error(message);
}
